import React, { useState } from "react";
import "../../styles/table.css"; // Import your CSS
import { formatDateTime } from "../../utils/formatDate";
import { formatHeader } from "../../functions/utils/util_functions";

interface Transaction {
	[key: string]: string | number | null;
}

interface Props {
	data: Transaction[];
}

const ITEMS_PER_PAGE = 10;

const TableComponent: React.FC<Props> = ({ data }) => {
	/**
	 * Use this component for data with several columns
	 * For data with less columns use the small table component
	 */
	const [currentPage, setCurrentPage] = useState(1);
	const [searchColumn, setSearchColumn] = useState<string>("");
	const [searchQuery, setSearchQuery] = useState<string>("");

	if (!data || data.length === 0) {
		return <div>No transactions found.</div>;
	}

	const headers = Object.keys(data[0]);

	const filteredData = data.filter((row) => {
		if (!searchColumn || !searchQuery) return true;
		const value = row[searchColumn];
		return value !== null &&
			value.toString().toLowerCase().includes(searchQuery.toLowerCase());
	});

	const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
	const paginatedData = filteredData.slice(
		(currentPage - 1) * ITEMS_PER_PAGE,
		currentPage * ITEMS_PER_PAGE
	);

	const goToPage = (page: number) => {
		if (page >= 1 && page <= totalPages) {
			setCurrentPage(page);
		}
	};

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(e.target.value);
		setCurrentPage(1);
	};

	return (
		<div className="table-component">
			<div className="table-search-controls">
				<select value={searchColumn} onChange={(e) => setSearchColumn(e.target.value)}>
					<option value="">Select column</option>
					{headers.map((header) => (
						<option key={header} value={header}>
							{formatHeader(header)}
						</option>
					))}
				</select>
				<input
					type="text"
					placeholder="Search value..."
					value={searchQuery}
					onChange={handleSearch}
				/>
			</div>
			<div>
				<table className="transaction-table">
					<thead>
						<tr>
							{headers.map((header) => (
								<th key={header}>{formatHeader(header)}</th>
							))}
						</tr>
					</thead>
					<tbody>
						{paginatedData.map((row, idx) => (
							<tr key={idx}>
								{headers.map((header) => (
									<td key={header}>
										{(() => {
											const cellValue = row[header];
											if (cellValue === null || cellValue === undefined) return "";

											const strValue = cellValue.toString();
											// If ISO timestamp → format it
											if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(strValue)) {
												return formatDateTime(strValue);
											}

											// ----These affect numbers on tables-----

											const numValue = Number(cellValue);

											if (!isNaN(numValue)) {
												// Round to 1 decimal
												const rounded = Math.round(numValue * 10) / 10;

												// Only show decimal if necessary
												return rounded % 1 === 0 ? rounded : rounded.toFixed(1);
											}

											return strValue;
										})()}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>

				<div className="table-pagination">
					<button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
						Previous
					</button>
					<span>Page {currentPage} of {totalPages}</span>
					<button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
						Next
					</button>
				</div>
			</div>
		</div>
	);
};

export default TableComponent;
